import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

const sessions = new WeakMap();
let authErrorCallback = null;
let pendingAuthError = null;

export const setTokenAuthErrorCallback = (callback) => {
  authErrorCallback = callback;
  if (callback && pendingAuthError) callback(pendingAuthError);
};

const getRequestKey = (account, request) => JSON.stringify([
  account?.homeAccountId,
  { ...request, account },
]);

export const resetTokenRecovery = (instance, request = null) => {
  const session = sessions.get(instance);
  if (request && session) {
    const account = request.account || instance.getActiveAccount();
    const key = getRequestKey(account, request);
    session.errors.delete(key);
    session.pending.delete(key);
    if (pendingAuthError?.request
      && getRequestKey(pendingAuthError.request.account, pendingAuthError.request)
        === getRequestKey(account, request)) {
      pendingAuthError = null;
    }
    return;
  }
  sessions.delete(instance);
  pendingAuthError = null;
};

const needsInteraction = (error) => error instanceof InteractionRequiredAuthError
  || ['interaction_required', 'login_required', 'consent_required', 'invalid_grant',
    'no_account_error', 'monitor_window_timeout'].includes(error?.errorCode)
  || /AADSTS(?:700084|50058)\b/i.test(error?.message || '');

export default async function getApiToken(instance, request = apiRequest) {
  let session = sessions.get(instance);
  if (!session) {
    session = { errors: new Map(), pending: new Map() };
    sessions.set(instance, session);
  }

  const account = request.account || instance.getActiveAccount();
  const tokenRequest = { ...request, account };
  const key = getRequestKey(account, tokenRequest);
  if (session.errors.has(key)) throw session.errors.get(key);
  if (session.pending.has(key)) return session.pending.get(key);

  const acquisition = (async () => {
    try {
      if (!account) throw new InteractionRequiredAuthError('no_account_error');
      const response = await instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      if (needsInteraction(error) && !session.errors.has(key)) {
        session.errors.set(key, error);
        const isConsentRequest = tokenRequest.scopes?.some((scope) =>
          scope.toLowerCase() === 'user.readbasic.all'
        ) || false;
        const authError = {
          type: 'AUTH_EXPIRED',
          isConsentRequest,
          message: isConsentRequest
            ? 'Additional sign-in is required to continue this request.'
            : 'Your session needs a new sign-in. Please sign in again to continue.',
          request: tokenRequest,
          requiresSignIn: true,
        };
        const existingRequiredSignIn = pendingAuthError && !pendingAuthError.isConsentRequest;
        if (!isConsentRequest || !existingRequiredSignIn) {
          pendingAuthError = authError;
          console.warn('[Auth] Interactive sign-in required:', error.errorCode);
          authErrorCallback?.(pendingAuthError);
        }
      }
      throw error;
    }
  })();
  session.pending.set(key, acquisition);
  try {
    return await acquisition;
  } finally {
    session.pending.delete(key);
  }
}
